import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./RejectedTalentPage.css";
import { Printer, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { printTableWithHeader } from "../../../utils/printUtils";
import { getFieldOptions } from "../../../utils/fieldsOptions";

const PAGE_SIZE = 30;
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function RejectedTalentPage() {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");
  const [rejectedTalents, setRejectedTalents] = useState([]);
  const [serverTime, setServerTime] = useState({ fullDateTime: "" });
  const [currentUser, setCurrentUser] = useState({ fullname: "", systemRole: "" });

  // Load current user
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) setCurrentUser({ fullname: user.fullname, systemRole: user.systemRole });
  }, []);

  // Fetch rejected talents & server time
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [talentRes, timeRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/talent/rejected`),
          axios.get(`${API_BASE_URL}/time/datetime`),
        ]);
        setRejectedTalents(talentRes.data || []);
        if (timeRes.data.success) setServerTime(timeRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setTimeout(() => setLoading(false), 1200);
      }
    };
    fetchData();
  }, []);

  // Filter + search
  const filteredTalents = useMemo(() => {
    return rejectedTalents.filter((t) => {
      const matchesSearch =
        t.name?.toLowerCase().includes(search.toLowerCase()) ||
        t.qualification?.toLowerCase().includes(search.toLowerCase());
      const matchesField = !fieldFilter || t.field === fieldFilter;
      return matchesSearch && matchesField;
    });
  }, [rejectedTalents, search, fieldFilter]);

  const totalPages = Math.ceil(filteredTalents.length / PAGE_SIZE);
  const paginatedData = filteredTalents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Update Status
  const handleStatusUpdate = async (talentId, newStatus) => {
    if (!window.confirm(`Are you sure you want to mark this as ${newStatus}?`)) return;

    try {
      await axios.patch(`${API_BASE_URL}/talent/${talentId}/status`, {
        status: newStatus,
        updatedBy: currentUser.fullname,
      });
      const res = await axios.get(`${API_BASE_URL}/talent/rejected`);
      setRejectedTalents(res.data || []);
      alert(`Talent status updated to ${newStatus}`);
    } catch (err) {
      console.error("Failed to update status:", err.message);
      alert("Failed to update talent status.");
    }
  };

  // Print
  const handlePrint = () => {
    printTableWithHeader({
      title: "Rejected Talent List",
      summary: "List of all rejected talents",
      columns: [
        { key: "sn", label: "SN" },
        { key: "name", label: "Name" },
        { key: "field", label: "Field" },
        { key: "qualification", label: "Qualification" },
        { key: "status", label: "Status" },
      ],
      rows: filteredTalents.map((t, idx) => ({
        sn: idx + 1,
        name: t.name,
        field: t.field,
        qualification: t.qualification,
        status: t.status,
      })),
    });
  };

  // Export Excel
  const handleExportExcel = () => {
    const dataForExcel = filteredTalents.map((t, idx) => ({
      SN: idx + 1,
      Name: t.name,
      Field: t.field,
      Qualification: t.qualification,
      Status: t.status,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RejectedTalents");
    XLSX.writeFile(workbook, "rejected_talents.xlsx");
  };

  return (
    <div className="pending-alumni-page">
      {/* USER INFO */}
      <div className="pending-user-card">
        <div className="pending-user-left">
          <div className="pending-user-name">Welcome, {currentUser.fullname}</div>
          <div className="pending-user-role">System Role: {currentUser.systemRole}</div>
        </div>
        <div className="pending-user-right">
          <div className="pending-user-date">{formatDate(serverTime.fullDateTime)}</div>
          <div className="pending-user-time">{formatTime(serverTime.fullDateTime)}</div>
        </div>
      </div>

      <h1 className="pending-page-title">Rejected Talent Pool</h1>

      {/* ACTION BUTTONS */}
      <div className="pending-action-row">
        <button className="pending-btn-primary" onClick={handlePrint}>
          <Printer size={16} /> Print
        </button>
        <button className="pending-btn-secondary" onClick={handleExportExcel}>
          <FileSpreadsheet size={16} /> Export
        </button>
      </div>

      {/* FILTER */}
      <div className="pending-filters">
        <select value={fieldFilter} onChange={(e) => setFieldFilter(e.target.value)}>
          <option value="">Filter by Field</option>
          {getFieldOptions().map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>

      {/* SEARCH */}
      <input
        className="pending-search-input"
        placeholder="Search by Name or Qualification"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* TABLE */}
      <div className="pending-table-card">
        <div className="pending-table-meta">
          <span>Total Rejected: {filteredTalents.length}</span>
          <span>Page {page} of {totalPages || 1}</span>
        </div>

        {loading ? (
          <div className="pending-loading">Loading rejected talents…</div>
        ) : (
          <table className="pending-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Name</th>
                <th>Field</th>
                <th>Qualification</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((t, idx) => (
                <tr key={t._id}>
                  <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                  <td>{t.name}</td>
                  <td>{t.field}</td>
                  <td>{t.qualification}</td>
                  <td>
                    <select
                      value={t.status} // display real status
                      onChange={(e) => handleStatusUpdate(t._id, e.target.value)}
                    >
                      <option value="rejected">REJECTED</option>
                      <option value="approved">APPROVED</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* PAGINATION */}
        <div className="pending-pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}